import type {
	ForestDisturbanceCreatePayload,
	ForestDisturbanceData,
	ForestDisturbanceDeletePayload,
	ForestDisturbancePayload,
	ForestDisturbanceResponse,
	ForestManagementPayload,
	ForestManagementResponse,
	ForestManagementT2,
	ModuleFull,
} from "@features/modules/forestManagement/types";
import {
	convertIncomingPercentage,
	convertOutgoingPercentage,
	convertEmptyStringsToNull,
	filterObjectProps
} from "@shared/modulePayload";
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createEntriesGetApi, createModuleGetApi } from "./factories/createModuleApi";

const includedPropList = [
	"id",
	"status",
	"land_use_change",
	"note",
	"area",
	"forest_condition_type",
	"land_use_type_start",
	"forest_type",
	"rotation_length_yrs_start",
	"rotation_length_yrs_w",
	"rotation_length_yrs_wo",
	"rotation_percentage_biomass_for_energy_start",
	"rotation_percentage_biomass_for_energy_w",
	"rotation_percentage_biomass_for_energy_wo",
	"logging_recurrence_yrs_start",
	"logging_recurrence_yrs_w",
	"logging_recurrence_yrs_wo",
	"logging_percentage_agb_logged_start",
	"logging_percentage_agb_logged_w",
	"logging_percentage_agb_logged_wo",
	"logging_percentage_biomass_for_energy_start",
	"logging_percentage_biomass_for_energy_w",
	"logging_percentage_biomass_for_energy_wo",
	"average_yearly_degradation_percentage_start",
	"average_yearly_degradation_percentage_w",
	"average_yearly_degradation_percentage_wo",
	"land_use_type_thread",
	"rotation_length_yrs_thread",
	"rotation_percentage_biomass_for_energy_thread",
	"logging_recurrence_yrs_thread",
	"logging_percentage_agb_logged_thread",
	"logging_percentage_biomass_for_energy_thread",
	"average_yearly_degradation_percentage_thread",
];
const includedT2PropList = [
	"soc_t2_start",
	"soc_t2_w",
	"soc_t2_wo",
	"agb_t2_start",
	"agb_t2_w",
	"agb_t2_wo",
	"bgb_t2_start",
	"bgb_t2_w",
	"bgb_t2_wo",
	"litter_t2_start",
	"litter_t2_w",
	"litter_t2_wo",
	"deadwood_t2_start",
	"deadwood_t2_w",
	"deadwood_t2_wo",
	"flu_t2_start",
	"flu_t2_w",
	"flu_t2_wo",
	"fi_t2_start",
	"fi_t2_w",
	"fi_t2_wo",
	"fmg_t2_start",
	"fmg_t2_w",
	"fmg_t2_wo",
	"agb_growth_rate_le_20_yrs_t2_start",
	"agb_growth_rate_le_20_yrs_t2_w",
	"agb_growth_rate_le_20_yrs_t2_wo",
	"agb_growth_rate_gt_20_yrs_t2_start",
	"agb_growth_rate_gt_20_yrs_t2_w",
	"agb_growth_rate_gt_20_yrs_t2_wo",
	"bgb_growth_rate_le_20_yrs_t2_start",
	"bgb_growth_rate_le_20_yrs_t2_w",
	"bgb_growth_rate_le_20_yrs_t2_wo",
	"bgb_growth_rate_gt_20_yrs_t2_start",
	"bgb_growth_rate_gt_20_yrs_t2_w",
	"bgb_growth_rate_gt_20_yrs_t2_wo",
	"agb_max_t2_start",
	"agb_max_t2_w",
	"agb_max_t2_wo",
	"bgb_max_t2_start",
	"bgb_max_t2_w",
	"bgb_max_t2_wo",
	"rotation_start_year_t2_start",
	"rotation_start_year_t2_w",
	"rotation_start_year_t2_wo",
	"logging_start_year_t2_start",
	"logging_start_year_t2_w",
	"logging_start_year_t2_wo",
	"logging_dry_matter_logged_t2_start",
	"logging_dry_matter_logged_t2_w",
	"logging_dry_matter_logged_t2_wo",
	"degradation_dry_matter_impacted_t2_start",
	"degradation_dry_matter_impacted_t2_w",
	"degradation_dry_matter_impacted_t2_wo",
	"data_source",
];

const MODULE_TYPE = "ForestManagement";
const TAG: TagTypes = "ForestManagement";
const TAG_DEFAULTS: TagTypes = "ForestManagementDefaults";
const ENDPOINT = FEModulesObj[FEModules.ForestManagement].path;
const percentageProps = [
	"rotation_percentage_biomass_for_energy",
	"logging_percentage_agb_logged",
	"logging_percentage_biomass_for_energy",
	"average_yearly_degradation_percentage",
];

const transformForestManagementResponse = (response: ForestManagementResponse) => {
	if (response) {
		const module = filterObjectProps(response, includedPropList) as ForestManagementPayload;
		const t2 = filterObjectProps(response, includedT2PropList) as ForestManagementT2;

		convertIncomingPercentage(module, percentageProps);
		return { parent: module, tiertwo: t2, disturbances: [] };
	}
	return null;
}

const forestManagementApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse: transformForestManagementResponse,
});

const publicForestManagementApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse: transformForestManagementResponse,
});

export const { useGetForestManagementQuery, useGetForestManagementDefaultsQuery } = forestManagementApiSlice;
export const {
	useGetForestManagementQuery: usePublicGetForestManagementQuery,
	useGetForestManagementDefaultsQuery: usePublicGetForestManagementDefaultsQuery
} = publicForestManagementApiSlice;

const ENTRY_MODULE_TYPE = "ForestDisturbances";
const TAG_ENTRY: TagTypes = "ForestDisturbance";
export const TAG_ENTRY_DEFAULTS: TagTypes = "ForestDisturbanceDefaults";
export const ENTRY_ENDPOINT = "forest-disturbances";

const transformForestDisturbanceResponse = (response: ForestDisturbanceData[]) => {
	if (response && response.length > 0) {
		for (const res of response)
			convertIncomingPercentage(res, ["percentage_biomass_destruction"])

		return response;
	}
	return [];
}

const forestDisturbanceApiSlice = createEntriesGetApi<ForestDisturbanceData[]>({
	apiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: transformForestDisturbanceResponse
});

const publicForestDisturbanceApiSlice = createEntriesGetApi<ForestDisturbanceData[]>({
	apiSlice: publicApiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: transformForestDisturbanceResponse
});

export const { useGetForestDisturbancesQuery, useGetForestDisturbancesDefaultsQuery } = forestDisturbanceApiSlice;
export const {
	useGetForestDisturbancesQuery: usePublicGetForestDisturbancesQuery,
	useGetForestDisturbancesDefaultsQuery: usePublicGetForestDisturbancesDefaultsQuery
} = publicForestDisturbanceApiSlice;

export const moduleApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		updateForestManagement: builder.mutation<
			ForestManagementResponse,
			ModuleFull
		>({
			query({ parent, tiertwo, activityId }) {
				const lucId = parent.land_use_change;

				convertOutgoingPercentage(parent, [
					"rotation_percentage_biomass_for_energy",
					"logging_percentage_agb_logged",
					"logging_percentage_biomass_for_energy",
					"average_yearly_degradation_percentage",
				]);

				const payload = {
					...parent,
					...tiertwo,
				} as unknown as ForestManagementResponse;
				const { id, ...rest } = payload;
				const body = { ...rest, land_use_change: lucId, activity: activityId };
				convertEmptyStringsToNull(body);
				return { url: `forest-managements/${id}/`, method: "PUT", body };
			},
			invalidatesTags: (_results, _error, { activityId, parent }) => [
				{ type: TAG, id: parent.id ?? undefined },
				{ type: "ModuleResults", id: parent.id ? `forest-managements-${parent.id}` : undefined },
				{ type: "ModuleHistory", id: parent.id ? `forest-managements-${parent.id}` : undefined },
				{ type: TAG_DEFAULTS, id: parent.id ?? undefined },
				{ type: 'ModuleResults', id: "land-use-changes" },
				{ type: "ModulesStatuses", id: activityId ?? undefined }
			],
		}),
		updateForestDisturbance: builder.mutation<
			ForestDisturbanceResponse,
			ForestDisturbancePayload
		>({
			query: ({ body }) => {
				const { id, ...rest } = body;
				const forestDisturbance = { ...rest };

				convertOutgoingPercentage(forestDisturbance, [
					"percentage_biomass_destruction",
				]);

				convertEmptyStringsToNull(forestDisturbance);
				return {
					url: `forest-disturbances/${id}/`,
					method: "PUT",
					body: forestDisturbance,
				};
			},
			invalidatesTags: (_results, _error, data) => [
				{ type: TAG_ENTRY, id: data.activityId },
				{ type: "ModuleResults", id: data.body.forest_management ? `forest-managements-${data.body.forest_management}` : undefined },
				{ type: 'ModuleResults', id: "land-use-changes" },
				{ type: "ModuleHistory", id: data.body.forest_management ? `forest-managements-${data.body.forest_management}` : undefined },
				{ type: TAG_ENTRY_DEFAULTS, id: data.body.id ? data.body.forest_management : undefined },
				{ type: "ModulesStatuses", id: data.activityId }
			],
		}),
		createForestDisturbance: builder.mutation<
			ForestDisturbanceResponse,
			ForestDisturbanceCreatePayload
		>({
			query: ({ body }) => ({
				url: "forest-disturbances/",
				method: "POST",
				body,
			}),
			invalidatesTags: (_results, _error, data) => [
				{ type: TAG_ENTRY, id: data.activityId },
				{ type: "ModuleResults", id: data.body.parent ? `forest-managements-${data.body.parent}` : undefined },
				{ type: 'ModuleResults', id: "land-use-changes" },
				{ type: "ModuleHistory", id: data.body.parent ? `forest-managements-${data.body.parent}` : undefined },
				{ type: "ModulesStatuses", id: data.activityId },
			],
		}),
		deleteForestDisturbance: builder.mutation<
			ForestDisturbanceResponse,
			ForestDisturbanceDeletePayload
		>({
			query: ({ id }) => ({
				url: `forest-disturbances/${id}/`,
				method: "DELETE",
			}),
			invalidatesTags: (_results, _error, data) => [
				{ type: TAG_ENTRY, id: data.activityId },
				{ type: "ModuleResults", id: data.parent ? `forest-managements-${data.parent}` : undefined },
				{ type: 'ModuleResults', id: "land-use-changes" },
				{ type: "ModuleHistory", id: data.parent ? `forest-managements-${data.parent}` : undefined },
				{ type: "ModulesStatuses", id: data.activityId },
			],
		}),
	}),
});

export const {
	useUpdateForestManagementMutation,
	useUpdateForestDisturbanceMutation,
	useCreateForestDisturbanceMutation,
	useDeleteForestDisturbanceMutation,
} = moduleApiSlice;

export const forestManagementEndpoints = forestManagementApiSlice.endpoints;
export const forestDisturbanceEndpoints = forestDisturbanceApiSlice.endpoints;
export const forestManagementMutationEndpoints = moduleApiSlice.endpoints;
